
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userAssignmentService } from '@/services/userAssignmentService';
import { affiliateDataConsistencyService } from '@/services/affiliateDataConsistencyService';
import type { DataConsistencyReport } from '@/services/userAssignmentService';
import type { ConsistencyReport } from '@/services/affiliateDataConsistencyService';
import { toast } from 'sonner';

export const useDataConsistencyReport = () => {
  return useQuery({
    queryKey: ['dataConsistencyReport'],
    queryFn: () => userAssignmentService.getDataConsistencyReport(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

export const useEnhancedConsistencyReport = () => {
  return useQuery({
    queryKey: ['enhancedConsistencyReport'],
    queryFn: () => affiliateDataConsistencyService.runConsistencyCheck(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

export const useFixDataInconsistencies = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (issues: DataConsistencyReport['issues']) => 
      userAssignmentService.fixDataInconsistencies(issues),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['dataConsistencyReport'] });
      queryClient.invalidateQueries({ queryKey: ['enhancedConsistencyReport'] });
      queryClient.invalidateQueries({ queryKey: ['unassignedUsers'] });
      queryClient.invalidateQueries({ queryKey: ['adminReferrals'] });
      
      if (result.fixed > 0) {
        toast.success(`Fixed ${result.fixed} data inconsistencies`);
      }
      if (result.failed > 0) {
        toast.error(`Failed to fix ${result.failed} issues`);
      }
    },
    onError: (error) => {
      console.error('Error fixing data inconsistencies:', error);
      toast.error('Failed to fix data inconsistencies');
    },
  });
};

export const useEnhancedAutoFix = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (report: ConsistencyReport) => 
      affiliateDataConsistencyService.autoFixIssues(report.issues),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['dataConsistencyReport'] });
      queryClient.invalidateQueries({ queryKey: ['enhancedConsistencyReport'] });
      queryClient.invalidateQueries({ queryKey: ['unassignedUsers'] });
      queryClient.invalidateQueries({ queryKey: ['adminReferrals'] });
      
      if (result.fixed > 0) {
        toast.success(`Enhanced auto-fix: Fixed ${result.fixed} issues`);
      }
      if (result.failed > 0) {
        toast.error(`Enhanced auto-fix: Failed to fix ${result.failed} issues`);
      }
      
      if (result.details.length > 0) {
        console.log('Auto-fix details:', result.details);
      }
    },
    onError: (error) => {
      console.error('Enhanced auto-fix error:', error);
      toast.error('Enhanced auto-fix failed');
    },
  });
};
